// settings/SettingsPage.tsx — COMPLETE WITH ALL MODULE PATHS
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';
import {
    Settings, Building2, Users, Shield, Key, Receipt,
    CreditCard, DollarSign, Banknote, Monitor, Table2,
    FileText, Hash, Tag, Store, Search, ChevronRight,
    TrendingUp, Warehouse, ClipboardList
} from 'lucide-react';
import * as api from './services/settingsApi';

// ============================================================
// SETTINGS MODULES CONFIG
// ============================================================
interface SettingsModule {
    id: string;
    path: string;
    label: string;
    description: string;
    icon: React.ElementType;
    gradient: string;
    iconBg: string;
    category: 'business' | 'people' | 'finance' | 'operations';
    stats: string[];
}

const SETTINGS_MODULES: SettingsModule[] = [
    {
        id: 'organization', path: '/settings/organization', label: 'Organization',
        description: 'Business overview, profile & configuration',
        icon: Store, gradient: 'from-emerald-500 to-teal-600', iconBg: 'bg-emerald-50 ',
        category: 'business', stats: ['Dashboard', 'Profile', 'Settings']
    },
    {
        id: 'branches', path: '/settings/branches', label: 'Branches',
        description: 'Manage business locations and main branch',
        icon: Building2, gradient: 'from-blue-500 to-indigo-600', iconBg: 'bg-blue-50 ',
        category: 'business', stats: ['Locations', 'Active', 'Main Branch']
    },
    {
        id: 'staff', path: '/settings/staff', label: 'Staff',
        description: 'Team members, roles & access management',
        icon: Users, gradient: 'from-purple-500 to-violet-600', iconBg: 'bg-purple-50 ',
        category: 'people', stats: ['Members', 'Active', 'Roles']
    },
    {
        id: 'roles', path: '/settings/roles', label: 'Roles & Permissions',
        description: 'Role-based access control & permissions',
        icon: Shield, gradient: 'from-amber-500 to-orange-600', iconBg: 'bg-amber-50 ',
        category: 'people', stats: ['Roles', 'Custom', 'System']
    },
    {
        id: 'permissions', path: '/settings/permissions', label: 'Permissions',
        description: 'View all system permissions by module',
        icon: Key, gradient: 'from-rose-500 to-pink-600', iconBg: 'bg-rose-50 ',
        category: 'people', stats: ['Permissions', 'Modules']
    },
    {
        id: 'taxes', path: '/settings/taxes', label: 'Tax Configuration',
        description: 'Tax rates, compound taxes & defaults',
        icon: Receipt, gradient: 'from-teal-500 to-cyan-600', iconBg: 'bg-teal-50 ',
        category: 'finance', stats: ['Tax Rates', 'Active', 'Default']
    },
    {
        id: 'currencies', path: '/settings/currencies', label: 'Currencies',
        description: 'Multi-currency support & exchange rates',
        icon: DollarSign, gradient: 'from-green-500 to-emerald-600', iconBg: 'bg-green-50 ',
        category: 'finance', stats: ['Currencies', 'Base']
    },
    {
        id: 'payment-methods', path: '/settings/payment-methods', label: 'Payment Methods',
        description: 'Accepted payment types & rules',
        icon: CreditCard, gradient: 'from-indigo-500 to-blue-600', iconBg: 'bg-indigo-50 ',
        category: 'finance', stats: ['Methods', 'Active']
    },
    {
        id: 'bank-accounts', path: '/settings/bank-accounts', label: 'Bank Accounts',
        description: 'Business bank accounts & balances',
        icon: Banknote, gradient: 'from-emerald-600 to-green-700', iconBg: 'bg-emerald-50 ',
        category: 'finance', stats: ['Accounts', 'Balance']
    },
    {
        id: 'discount-reasons', path: '/settings/discount-reasons', label: 'Discount Reasons',
        description: 'Preset discount reasons & approval rules',
        icon: Tag, gradient: 'from-violet-500 to-purple-600', iconBg: 'bg-violet-50 ',
        category: 'finance', stats: ['Reasons', 'Active']
    },
    {
        id: 'inventory', path: '/settings/organization/inventory', label: 'Inventory Settings',
        description: 'Stock control, transfers & approvals',
        icon: Warehouse, gradient: 'from-amber-500 to-orange-600', iconBg: 'bg-amber-50 ',
        category: 'operations', stats: ['Stock', 'Transfers', 'Approvals']
    },
    {
        id: 'procurement', path: '/settings/organization/procurement', label: 'Procurement Settings',
        description: 'Requisitions, POs & receiving rules',
        icon: ClipboardList, gradient: 'from-rose-500 to-pink-600', iconBg: 'bg-rose-50 ',
        category: 'operations', stats: ['Requisitions', 'POs', 'Receiving']
    },
    {
        id: 'terminals', path: '/settings/terminals', label: 'POS Terminals',
        description: 'Point of sale devices & status',
        icon: Monitor, gradient: 'from-cyan-500 to-sky-600', iconBg: 'bg-cyan-50 ',
        category: 'operations', stats: ['Devices', 'Online', 'Active']
    },
    {
        id: 'tables', path: '/settings/tables', label: 'Table Management',
        description: 'Restaurant table layout & status',
        icon: Table2, gradient: 'from-orange-500 to-red-600', iconBg: 'bg-orange-50 ',
        category: 'operations', stats: ['Tables', 'Available']
    },
    {
        id: 'receipt-templates', path: '/settings/receipt-templates', label: 'Receipt Templates',
        description: 'Customizable receipt print layouts',
        icon: FileText, gradient: 'from-pink-500 to-rose-600', iconBg: 'bg-pink-50 ',
        category: 'operations', stats: ['Templates', 'Default']
    },
    {
        id: 'number-sequences', path: '/settings/number-sequences', label: 'Number Sequences',
        description: 'Auto-numbering for invoices & orders',
        icon: Hash, gradient: 'from-slate-500 to-slate-700', iconBg: 'bg-slate-50 ',
        category: 'operations', stats: ['Sequences', 'Active']
    },
];

// ============================================================
// MAIN COMPONENT
// ============================================================

export function SettingsPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<string>('all');

    const { data: branches } = useQuery({ queryKey: ['branches', user?.tenantId], queryFn: () => api.getBranches(user!.tenantId), enabled: !!user?.tenantId });
    const { data: staff } = useQuery({ queryKey: ['staff', user?.tenantId], queryFn: () => api.getStaff(user!.tenantId), enabled: !!user?.tenantId });
    const { data: taxes } = useQuery({ queryKey: ['taxes', user?.tenantId], queryFn: () => api.getTaxes(user!.tenantId), enabled: !!user?.tenantId });
    const { data: terminals } = useQuery({ queryKey: ['pos-terminals', user?.tenantId], queryFn: () => api.getPosTerminals(user!.tenantId), enabled: !!user?.tenantId });

    const stats = {
        branchesCount: branches?.length || 0,
        staffCount: staff?.data?.length || 0,
        taxesCount: taxes?.length || 0,
        terminalsCount: terminals?.length || 0,
        activeBranches: branches?.filter((b: any) => b.isActive).length || 0,
        activeStaff: staff?.data?.filter((s: any) => s.isActive).length || 0,
    };

    const filteredModules = useMemo(() => {
        return SETTINGS_MODULES.filter(m => {
            if (categoryFilter !== 'all' && m.category !== categoryFilter) return false;
            if (searchQuery) {
                const q = searchQuery.toLowerCase();
                return m.label.toLowerCase().includes(q) || m.description.toLowerCase().includes(q);
            }
            return true;
        });
    }, [searchQuery, categoryFilter]);

    const categories = [
        { id: 'all', label: 'All Settings' },
        { id: 'business', label: 'Business' },
        { id: 'people', label: 'People' },
        { id: 'finance', label: 'Finance' },
        { id: 'operations', label: 'Operations' },
    ];

    return (
        <div className="min-h-screen">
            <div className="max-w-[1600px] mx-auto p-4 lg:p-6 space-y-6">

                {/* ========== HEADER ========== */}
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                    <p className="text-slate-400 text-sm">
                        {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                    <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 mt-1">
                        ⚙️ Settings & Configuration
                    </h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Manage your business — {stats.branchesCount} branches, {stats.staffCount} staff, {stats.taxesCount} tax rates
                    </p>
                </motion.div>

                {/* ========== QUICK STATS ========== */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                        { label: 'Branches', value: stats.branchesCount, sub: `${stats.activeBranches} active`, icon: Building2, gradient: 'from-blue-500 to-indigo-600', accent: 'text-blue-600 ' },
                        { label: 'Staff Members', value: stats.staffCount, sub: `${stats.activeStaff} active`, icon: Users, gradient: 'from-purple-500 to-violet-600', accent: 'text-purple-600 ' },
                        { label: 'Tax Rates', value: stats.taxesCount, sub: 'configured', icon: Receipt, gradient: 'from-teal-500 to-cyan-600', accent: 'text-teal-600 ' },
                        { label: 'POS Terminals', value: stats.terminalsCount, sub: 'registered', icon: Monitor, gradient: 'from-cyan-500 to-sky-600', accent: 'text-cyan-600 ' },
                    ].map((stat, i) => (
                        <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                            whileHover={{ y: -3 }}
                            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:shadow-lg transition-all duration-300">
                            <div className={`p-2.5 rounded-xl bg-gradient-to-br ${stat.gradient} shadow-md w-fit mb-3`}>
                                <stat.icon className="w-5 h-5 text-white" />
                            </div>
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{stat.label}</p>
                            <p className={`text-2xl lg:text-3xl font-bold mt-1 tracking-tight ${stat.accent}`}>{stat.value}</p>
                            <p className="text-xs text-slate-400 mt-1">{stat.sub}</p>
                        </motion.div>
                    ))}
                </div>

                {/* ========== SEARCH & FILTERS ========== */}
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1 max-w-md">
                        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input type="text" placeholder="Search settings..." value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-11 pr-4 py-3 border-2 border-slate-200 rounded-2xl text-sm bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 transition-all" />
                    </div>
                    <div className="flex gap-1.5 bg-slate-100 rounded-xl p-1 overflow-x-auto">
                        {categories.map((cat) => (
                            <button key={cat.id} onClick={() => setCategoryFilter(cat.id)}
                                className={clsx('px-4 py-2 rounded-lg text-xs font-bold whitespace-nowrap transition-all',
                                    categoryFilter === cat.id
                                        ? 'bg-white text-emerald-600 shadow-sm'
                                        : 'text-slate-500 hover:text-slate-700 :text-slate-300')}>
                                {cat.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* ========== MODULES GRID ========== */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {filteredModules.map((module, i) => (
                        <motion.button key={module.id}
                            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                            whileHover={{ y: -4, scale: 1.02 }} whileTap={{ scale: 0.98 }}
                            onClick={() => navigate(module.path)}
                            className="group relative text-left p-5 rounded-2xl border-2 border-slate-200 bg-white hover:border-emerald-300 :border-emerald-700 hover:shadow-lg transition-all duration-300 overflow-hidden">
                            <div className="relative z-10">
                                <div className="flex items-start justify-between mb-3">
                                    <div className={`p-2.5 rounded-xl ${module.iconBg} group-hover:shadow-md transition-all duration-300 group-hover:scale-110`}>
                                        <module.icon className="w-5 h-5 text-slate-500 group-hover:text-slate-700 :text-slate-300 transition-colors" />
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-500 group-hover:translate-x-1 transition-all" />
                                </div>
                                <h3 className="font-semibold text-slate-900 text-sm mb-1">{module.label}</h3>
                                <p className="text-xs text-slate-500 line-clamp-2">{module.description}</p>
                                <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100 ">
                                    {module.stats.map((stat, j) => (
                                        <span key={j} className="text-[10px] font-medium text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full">{stat}</span>
                                    ))}
                                </div>
                            </div>
                        </motion.button>
                    ))}
                </div>

                {filteredModules.length === 0 && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20">
                        <div className="w-20 h-20 bg-slate-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
                            <Search className="w-10 h-10 text-slate-300" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-700 ">No settings found</h3>
                        <p className="text-slate-400 mt-2">Try adjusting your search or filters</p>
                        <button onClick={() => { setSearchQuery(''); setCategoryFilter('all'); }}
                            className="mt-4 text-emerald-600 hover:text-emerald-700 font-medium text-sm">Clear all filters</button>
                    </motion.div>
                )}
            </div>
        </div>
    );
}